import { noop } from "lodash/fp";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type { CalendarPermissionLevel } from "@app/analytics/types";
import useAuth from "@app/common/auth/useAuth";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import TooltipWrapper from "@rui/TooltipWrapper";

import { SETTING_COPY } from "./constants";
import { Setting, SettingRow } from "./styles";

const JOIN_PREFERENCE_SETTINGS: {
  value: CalendarPermissionLevel;
  label: string;
}[] = [
  { value: "all", label: "All calendar events" },
  {
    value: "hosting_only",
    label: "Calendar events I host",
  },
  {
    value: "invite_only",
    label: "Only when invited",
  },
];

const ONLY_JOIN_INTERNAL_OPTIONS = [
  { value: "false", label: "Internal and external" },
  { value: "true", label: "Internal only" },
];

const JoinPreferenceSettings = () => {
  const { user } = useAuth();
  const { userSettings, updateUserSettings } = useUserSettings();

  const enabledJoinPreferenceSettingOptions =
    UserSettingsUtils.enabledCalendarPermissionLevelsForUser(user);

  const joinPreferencesDisabledOpts = UserSettingsUtils.getSettingDisabledOpts({
    settingIsEditable: enabledJoinPreferenceSettingOptions.length > 1,
  });

  const canEditCalendarOnlyJoinInternal =
    UserSettingsUtils.canEditCalendarJoinInternal(user);

  const handleUpdateCalendarPermissionLevel = (
    value: CalendarPermissionLevel
  ) => {
    updateUserSettings.mutate(
      { calendarPermissionLevel: value },
      { onSuccess: noop } // don't show toast on success
    );
  };

  const handleUpdateOnlyJoinInternal = (value: string) => {
    updateUserSettings.mutate(
      { calendarOnlyJoinInternal: value === "true" },
      { onSuccess: noop } // don't show toast on success
    );
  };

  return (
    <SettingRow>
      <Setting {...SETTING_COPY.calendarPermissionLevel}>
        <TooltipWrapper
          title={joinPreferencesDisabledOpts.tooltip}
          show={joinPreferencesDisabledOpts.disabled}
          wrapperSx={{ width: "100%" }}
        >
          <Dropdown
            name="read-assistant-will-join-select"
            disabled={joinPreferencesDisabledOpts.disabled}
            selectedItem={getSelectedFromOptions(
              JOIN_PREFERENCE_SETTINGS,
              userSettings.calendarPermissionLevel
            )}
            onChange={(event) =>
              handleUpdateCalendarPermissionLevel(
                event.target.value as CalendarPermissionLevel
              )
            }
            fullWidth
          >
            {JOIN_PREFERENCE_SETTINGS.map(({ value, label }) => {
              if (!enabledJoinPreferenceSettingOptions.includes(value)) {
                return null;
              }

              return (
                <DropdownMenuItem key={value} value={value} label={label} />
              );
            })}
          </Dropdown>
        </TooltipWrapper>
      </Setting>
      {canEditCalendarOnlyJoinInternal && (
        <Setting {...SETTING_COPY.calendarOnlyJoinInternal}>
          <Dropdown
            name="participant-type-select"
            selectedItem={getSelectedFromOptions(
              ONLY_JOIN_INTERNAL_OPTIONS,
              `${userSettings.calendarOnlyJoinInternal}`
            )}
            onChange={(event) =>
              handleUpdateOnlyJoinInternal(event.target.value)
            }
            fullWidth
          >
            {ONLY_JOIN_INTERNAL_OPTIONS.map(({ value, label }) => (
              <DropdownMenuItem key={value} value={value} label={label} />
            ))}
          </Dropdown>
        </Setting>
      )}
    </SettingRow>
  );
};

export default JoinPreferenceSettings;
